import React, { useEffect, useRef, useState } from 'react';
import { GraduationCap, Zap, Sparkles, Layers, ListChecks, ArrowRight, Play, Pause, Check, Clock, ExternalLink, Gift } from 'lucide-react';

// Snaptrude creator-page personas. First shown on the 2026-10-19 call with Uzma.
// Updated 2026-11-03 after the 2026-10-30 call: cast approved (Architect voice = Option 2),
// page roles (3 E's), student plan angle, "if X designed it" format, first posts by 11 Nov,
// and the live checklist of what we still need from her.
// No pricing, no production IDs (those stay in clients-current/snaptrude/avatars/registry.json). No em dashes in copy.

const A = '/assets/snaptrude/cast';
const NOTION_HUB = 'https://app.notion.com/p/Snaptrude-SocialSEO-4003ac2a5309819bb786c150dfa6d309';
const CAST_PAGE = '/snaptrude-cast';

const PERSONAS = [
    {
        key: 'auth', tag: 'Education', title: 'The Professor', sub: 'Teaches architecture, 25+ years in studio', Icon: GraduationCap,
        ring: 'border-sky-400/30', chip: 'bg-sky-500/15 text-sky-300', glow: 'from-sky-500/15', btn: 'bg-sky-500 hover:bg-sky-400',
        face: `${A}/professor-1.jpg`, audio: `${A}/professor.mp3`,
        voice: 'Calm. Like a favourite teacher.',
        formats: 'Lessons to camera, famous building critiques, AI myths.',
        hooks: ['Will AI take an architect\'s job? My students ask me every year.', 'The first hour of a project decides more than the next six months.'],
    },
    {
        key: 'loud', tag: 'Education + Entertainment', title: 'The Tech Graduate', sub: 'Recent grad who knows the tools better than his bosses', Icon: Zap,
        ring: 'border-orange-400/30', chip: 'bg-orange-500/15 text-orange-300', glow: 'from-orange-500/15', btn: 'bg-orange-500 hover:bg-orange-400',
        face: `${A}/techgrad-1.jpg`, audio: `${A}/techgrad.mp3`,
        voice: 'Fast and a little cheeky.',
        formats: 'Tool vs tool tests, famous-character builds, speed runs.',
        hooks: ['If Batman gave me 10 seconds to design his house, this is how I would do it.', 'My boss spent two days on this. I did it before lunch.'],
    },
    {
        key: 'aes', tag: 'Entertainment + Emotion', title: 'The Aspiring Architect', sub: 'Final-year student building her portfolio', Icon: Sparkles,
        ring: 'border-fuchsia-400/30', chip: 'bg-fuchsia-500/15 text-fuchsia-300', glow: 'from-fuchsia-500/15', btn: 'bg-fuchsia-500 hover:bg-fuchsia-400',
        face: `${A}/architect-main.jpg`, audio: `${A}/architect-eliza.mp3`,
        voice: 'Warm, like a friend talking.',
        formats: 'Uni stories, studio life, portfolio glow-ups.',
        hooks: ['This is what happened in my university last week.', 'My professor said my portfolio had no story. Honestly? She was right.'],
    },
];

const DECIDED = [
    'All three faces and voices approved. The Aspiring Architect uses voice Option 2.',
    'Snaptrude shows up as a step in the work, never as an ad.',
    'The Aspiring Architect promotes the free student plan.',
    '30 reels a month per page. First posts by 11 November.',
];


const REACH = [
    { who: 'The Aspiring Architect', w: 'w-full', cls: 'bg-fuchsia-500', note: 'Most reach' },
    { who: 'The Tech Graduate', w: 'w-2/3', cls: 'bg-orange-500', note: 'Middle' },
    { who: 'The Professor', w: 'w-1/3', cls: 'bg-sky-500', note: 'Most focused audience' },
];

const STEPS = [
    { t: 'Creators approved', done: true },
    { t: 'Looks and voices approved', done: true },
    { t: 'Your script feedback in Notion', now: true },
    { t: 'We lock the visual identity' },
    { t: 'Pages go live, 11 Nov' },
    { t: 'First 30-day review' },
];

const NEEDS = [
    { t: 'Notion access', d: 'Sent to uzma@snaptrude.com.', done: true },
    { t: 'Feedback on the first scripts', d: 'Leave it on each script in Notion.' },
    { t: 'Re-share the Google Drive', d: "To suresh@socialseo.in. B-roll, Altaf's videos and the HTML file." },
    { t: 'A Snaptrude login', d: 'Full access, on socialseowork@gmail.com, for our screen recordings.' },
    { t: "Altaf's videos and audio", d: 'Interviews or podcasts, plus his OK for the clone.' },
];

const SectionTag = ({ icon: Icon, children }) => (
    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-indigo-300">
        <Icon size={14} /> {children}
    </div>
);

const Row = ({ k, v }) => (
    <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{k}</div>
        <div className="text-sm text-slate-300 leading-relaxed mt-0.5">{v}</div>
    </div>
);

export default function SnaptrudePersonas() {
    useEffect(() => { document.title = 'SocialSEO | Snaptrude Creator Pages'; }, []);
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(null);

    const toggle = (id, src) => {
        const el = audioRef.current;
        if (!el) return;
        if (playing === id) { el.pause(); setPlaying(null); return; }
        el.src = src;
        el.currentTime = 0;
        el.play().then(() => setPlaying(id)).catch(() => setPlaying(null));
    };

    const open = NEEDS.filter((n) => !n.done).length;

    return (
        <div className="min-h-screen text-white">
            <audio ref={audioRef} onEnded={() => setPlaying(null)} preload="none" />
            <header className="fixed top-4 inset-x-0 z-50 flex justify-center px-4">
                <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/40 backdrop-blur px-4 py-2 text-xs">
                    <span className="font-black">Social<span className="text-indigo-400">[SEO]</span></span>
                    <span className="text-slate-500">×</span>
                    <span className="text-slate-300">Snaptrude</span>
                    <span className="ml-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-300 font-bold">Live 11 Nov</span>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-28 pb-24 flex flex-col gap-20">

                <section className="flex flex-col gap-5">
                    <SectionTag icon={Layers}>Updated 3 November</SectionTag>
                    <h1 className="text-4xl sm:text-6xl font-black leading-[1.05] bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                        Your three creators are ready.
                    </h1>
                    <p className="text-lg text-slate-400 max-w-3xl">
                        Three AI creator pages for architects, from students to studio heads. Here is where we are and what we need from you.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <a href={NOTION_HUB} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 px-5 py-2.5 text-sm font-bold text-white transition">
                            Scripts and feedback in Notion <ExternalLink size={15} />
                        </a>
                        <a href={CAST_PAGE} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 px-5 py-2.5 text-sm font-bold text-white transition">
                            See every look full screen <ArrowRight size={15} />
                        </a>
                    </div>
                </section>

                <section className="grid lg:grid-cols-3 gap-5">
                    {PERSONAS.map((p) => {
                        const on = playing === p.key;
                        return (
                            <article key={p.key} className={`rounded-3xl border ${p.ring} bg-white/5 backdrop-blur overflow-hidden flex flex-col`}>
                                <div className="relative">
                                    <img src={p.face} alt={p.title} className="w-full aspect-[4/5] object-cover object-top" loading="lazy" />
                                    <span className="absolute top-4 left-4 rounded-full bg-black/60 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur">{p.tag}</span>
                                    <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white"><Check size={12} /> Approved</span>
                                </div>
                                <div className={`p-6 bg-gradient-to-b ${p.glow} to-transparent flex flex-col gap-4`}>
                                    <div className="flex items-center gap-4">
                                        <div className={`h-12 w-12 shrink-0 rounded-2xl flex items-center justify-center ${p.chip}`}><p.Icon size={22} /></div>
                                        <div className="min-w-0">
                                            <h3 className="text-2xl font-black">{p.title}</h3>
                                            <div className="text-sm text-slate-400">{p.sub}</div>
                                        </div>
                                    </div>
                                    <button type="button" onClick={() => toggle(p.key, p.audio)} className={`self-start inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white transition ${p.btn}`}>
                                        {on ? <Pause size={16} /> : <Play size={16} />} {on ? 'Pause' : 'Hear the voice'}
                                    </button>
                                </div>
                                <div className="p-6 pt-2 flex flex-col gap-4">
                                    <Row k="Voice" v={p.voice} />
                                    <Row k="Formats" v={p.formats} />
                                    <div>
                                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Sample hooks</div>
                                        <ul className="flex flex-col gap-2">
                                            {p.hooks.map((h) => (
                                                <li key={h} className="rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-slate-200">{h}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </section>

                <section className="flex flex-col gap-5">
                    <SectionTag icon={Check}>Agreed on 30 October</SectionTag>
                    <ul className="grid sm:grid-cols-2 gap-3">
                        {DECIDED.map((d) => (
                            <li key={d} className="flex gap-3 items-start rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-slate-200">
                                <Check size={18} className="mt-0.5 shrink-0 text-emerald-400" />
                                <span>{d}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                <section className="flex flex-col gap-5">
                    <SectionTag icon={Layers}>Expected reach, month one</SectionTag>
                    <div className="flex flex-col gap-3 max-w-3xl">
                        {REACH.map((r) => (
                            <div key={r.who} className="flex flex-col gap-1.5">
                                <div className="flex justify-between text-sm"><span className="font-bold">{r.who}</span><span className="text-slate-400">{r.note}</span></div>
                                <div className="h-3 rounded-full bg-white/10 overflow-hidden"><div className={`h-full rounded-full ${r.w} ${r.cls}`} /></div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="flex flex-col gap-5">
                    <SectionTag icon={Layers}>Content mix on every page</SectionTag>
                    <div className="grid grid-cols-1 sm:grid-cols-[7fr_3fr] rounded-2xl overflow-hidden border border-white/10 text-sm">
                        <div className="bg-white/5 p-4"><b>7 in 10:</b> pure value. Tips, stories, challenges.</div>
                        <div className="bg-indigo-500 p-4"><b>3 in 10:</b> Snaptrude in use.</div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 flex flex-col gap-1">
                            <div className="text-xs font-bold uppercase tracking-[0.15em] text-orange-300">Famous-character builds</div>
                            <p className="text-slate-300 text-sm">"If Batman designed his house", built live in Snaptrude.</p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 flex flex-col gap-1">
                            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-fuchsia-300"><Gift size={14} /> Free for students</div>
                            <p className="text-slate-300 text-sm">The Aspiring Architect tells students the plan is free.</p>
                        </div>
                    </div>
                </section>

                <section className="flex flex-col gap-5">
                    <SectionTag icon={ArrowRight}>Timeline</SectionTag>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {STEPS.map((s, i) => (
                            <div key={s.t} className={`rounded-2xl border p-5 flex flex-col gap-2 ${s.now ? 'border-indigo-400 bg-indigo-500/10' : 'border-white/10 bg-white/5'}`}>
                                <div className="flex items-center justify-between">
                                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-indigo-300">Step {i + 1}</div>
                                    {s.done && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-300"><Check size={11} /> Done</span>}
                                    {s.now && <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white"><Clock size={11} /> Now</span>}
                                </div>
                                <div className="text-lg font-bold">{s.t}</div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="flex flex-col gap-5">
                    <SectionTag icon={ListChecks}>What we need from you</SectionTag>
                    <h2 className="text-3xl sm:text-4xl font-black">{open} things left before 11 November.</h2>
                    <ul className="grid sm:grid-cols-2 gap-3">
                        {NEEDS.map((n) => (
                            <li key={n.t} className={`flex gap-3 items-start rounded-xl border px-4 py-3 ${n.done ? 'border-emerald-400/30 bg-emerald-500/5' : 'border-white/10 bg-white/5'}`}>
                                {n.done
                                    ? <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-emerald-500 text-white"><Check size={14} /></span>
                                    : <span className="mt-1 h-4 w-4 shrink-0 rounded border-2 border-indigo-400" />}
                                <span className="min-w-0">
                                    <span className={`block font-bold ${n.done ? 'text-emerald-200' : 'text-white'}`}>{n.t}{n.done && ' · done'}</span>
                                    <span className="block text-sm text-slate-400 leading-relaxed">{n.d}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            </main>
        </div>
    );
}
